import { useEffect, useState } from "react";
import {
  useFlagsStatus,
  useUnleashClient,
  useUnleashContext
} from "@unleash/proxy-client-react";

import { logger } from "~/services/logger";

type UseInitFeatureFlagsContextParams = {
  tillId?: string | null;
};

type UseInitFeatureFlagsContextReturn = {
  featureFlagsContextLoading: boolean;
  featureFlagsContextError: Error | null;
  featureFlagsContextReady: boolean;
};

/**
 * Custom hook to initialize Unleash feature flags context and manage readiness state.
 *
 * This hook solves the UI shifting issue that occurs when feature flag values change
 * after setting the Unleash context. It ensures that components wait for both the
 * context to be set AND the flags to be ready before rendering flag-dependent UI.
 *
 * @param params - Configuration object containing context parameters
 * @param params.tillId - The till ID to include in the Unleash context for targeting
 *
 * @returns {UseInitFeatureFlagsContextReturn} Object containing loading state, error state, and readiness state
 *
 * @example
 * ```typescript
 * const MyComponent = () => {
 *   const { data: till } = useCurrentTill();
 *   const { featureFlagsContextLoading } = useInitFeatureFlagsContext({
 *     tillId: till?.id
 *   });
 *   const showNewFeature = useFlag('new-feature');
 *
 *   if (featureFlagsContextLoading) {
 *     return <LoadingSpinner />; // Prevent flash while context loads
 *   }
 *
 *   return showNewFeature ? <NewFeature /> : <OldFeature />;
 * };
 * ```
 *
 * @see {@link https://docs.getunleash.io/reference/sdks/react | Unleash React SDK Documentation}
 */
export const useInitFeatureFlagsContext = ({
  tillId
}: UseInitFeatureFlagsContextParams): UseInitFeatureFlagsContextReturn => {
  const updateContext = useUnleashContext();
  const unleashClient = useUnleashClient();
  const { flagsReady } = useFlagsStatus();

  const [isLoading, setIsLoading] = useState(true);
  const [featureFlagsContextError, setFeatureFlagsContextError] =
    useState<Error | null>(null);

  useEffect(() => {
    unleashClient.on("error", (error: Error) => {
      logger.error(error, "unleash error");
      setFeatureFlagsContextError(error);
    });

    unleashClient.off("error", () => {
      setFeatureFlagsContextError(null);
    });
  }, [unleashClient]);

  // Update the Unleash context with the provided till ID
  useEffect(() => {
    const run = async () => {
      if (tillId) {
        setIsLoading(true);
        await updateContext({ properties: { tillId } });
        setIsLoading(false);
      } else {
        setIsLoading(false);
      }
    };

    run();
  }, [tillId, updateContext]);

  return {
    featureFlagsContextLoading: isLoading,
    featureFlagsContextReady:
      !isLoading && flagsReady && !featureFlagsContextError,
    featureFlagsContextError
  };
};
